import { useCallback, useEffect, useRef, type ReactNode } from "react";

// The scroller a wide table lives in (task a3e8f4dd). Pair it with `data-fixed-table` on the
// <table> itself: the attribute carries the three regimes (fixed layout, frozen first column,
// card regime) and this component owns the one piece of them a stylesheet cannot observe —
// whether the scroller is currently scrolled.
//
// Two things it is NOT:
//  * NOT a place for a table's look. The regimes compose with whatever look class the table
//    already carries; this element only clips and scrolls.
//  * NOT decoration. The frozen column's divider appears only while `data-scrolled="true"`,
//    which means "there are columns to the left of this one that you cannot see right now".
//
// The scrollbar-owning element is focusable (tabIndex 0) with a role and an accessible name,
// because a region a mouse can scroll must also be reachable by keyboard — WCAG 2.1.1.

export interface TableScrollProps {
  /** Names the scrollable region. Use the table's own caption/heading text. */
  label: string;
  className?: string;
  "data-testid"?: string;
  children: ReactNode;
}

export function TableScroll({ label, className, children, ...rest }: TableScrollProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.dataset.scrolled = String(el.scrollLeft > 0);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    // A resize can end the overflow entirely (or rows can arrive and start it), and neither
    // fires a scroll event — the state would stay stale and the divider would lie.
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(sync);
    ro?.observe(el);
    return () => {
      el.removeEventListener("scroll", sync);
      ro?.disconnect();
    };
  }, [sync]);

  return (
    <div
      ref={ref}
      className={className ? `table-scroll ${className}` : "table-scroll"}
      data-scrolled="false"
      role="region"
      aria-label={label}
      // A scrollable region must be keyboard-operable (WCAG 2.1.1; axe "scrollable-region-focusable").
      // The rule's allow-list only knows tabpanel; a named scrollable region is the same case.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      data-testid={rest["data-testid"]}
    >
      {children}
    </div>
  );
}
